import type {
  AvatarResponse,
  MfaEnrollConfirmResponse,
  MfaEnrollStartResponse,
  MfaRequirementReason,
  MfaStatusResponse,
  TrustedDevice,
} from '@platform/contracts';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@platform/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, Download, Laptop, ShieldCheck } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { useState } from 'react';
import { toast } from 'sonner';

import { api, errorMessage } from '../lib/api';
import { fmtDateTime } from '../lib/console';
import { useMe } from '../lib/queries';
import { Field, FormError, fd } from './form';
import { ImagePicker } from './image-picker';

/** Por qué la verificación en dos pasos es obligatoria, en una frase. */
export function mfaReasonText(reasons: readonly MfaRequirementReason[]): string {
  const r = reasons[0];
  if (!r) return '';
  if (r.kind === 'superadmin') return 'Eres superadmin: no se puede desactivar.';
  if (r.kind === 'agency') return 'Tu rol en la agencia la exige: no se puede desactivar.';
  return `${r.clientName} la exige a todo su equipo: no puedes desactivarla mientras esa política siga activa.`;
}

const MFA_STATUS_KEY = ['mfa-status'] as const;
const TRUSTED_DEVICES_KEY = ['trusted-devices'] as const;

/** Activar desde el perfil: QR → código → códigos de recuperación. */
function EnrollSteps({ onDone }: { onDone: () => void }) {
  const [codes, setCodes] = useState<string[] | null>(null);
  const start = useQuery({
    queryKey: ['mfa-enroll-profile'],
    queryFn: () => api<MfaEnrollStartResponse>('/api/auth/mfa/enroll/start', { method: 'POST' }),
    staleTime: Infinity,
    gcTime: 0,
    retry: false,
  });
  const confirm = useMutation({
    mutationFn: (code: string) =>
      api<MfaEnrollConfirmResponse>('/api/auth/mfa/enroll/confirm', {
        method: 'POST',
        body: { code },
      }),
    onSuccess: (res) => {
      setCodes(res.recoveryCodes);
    },
  });

  if (codes) {
    const text = codes.join('\n');
    return (
      <div className="flex flex-col gap-3">
        <p className="text-sm font-medium">Guarda tus códigos de recuperación</p>
        <p className="text-sm text-muted-foreground">
          Se muestran una sola vez. Cada uno te sirve para entrar una vez si pierdes el teléfono.
        </p>
        <ul
          aria-label="Códigos de recuperación"
          className="grid grid-cols-2 gap-2 rounded-md border bg-muted p-3 font-mono text-sm"
        >
          {codes.map((c) => (
            <li key={c}>{c}</li>
          ))}
        </ul>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              void navigator.clipboard.writeText(text).then(() => toast.success('Copiados'));
            }}
          >
            <Copy /> Copiar
          </Button>
          <Button variant="outline" size="sm" asChild>
            <a
              href={`data:text/plain;charset=utf-8,${encodeURIComponent(text)}`}
              download="codigos-recuperacion-metamindia.txt"
            >
              <Download /> Descargar
            </a>
          </Button>
          <Button size="sm" className="ml-auto" onClick={onDone}>
            Ya los guardé
          </Button>
        </div>
      </div>
    );
  }

  if (!start.data) {
    return start.error ? (
      <FormError message={errorMessage(start.error)} />
    ) : (
      <p className="text-sm text-muted-foreground">Preparando el código QR…</p>
    );
  }
  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-muted-foreground">
        Escanea el código con Google Authenticator, 1Password o una app similar y escribe el código
        de 6 dígitos que te muestre.
      </p>
      <div className="flex justify-center rounded-md bg-white p-4">
        <QRCodeSVG
          value={start.data.otpauthUrl}
          size={160}
          aria-label="Código QR para la app de autenticación"
        />
      </div>
      <p className="text-center text-xs text-muted-foreground">
        ¿No puedes escanearlo? Escribe esta clave en la app:
      </p>
      <p data-testid="mfa-secret" className="break-all text-center font-mono text-xs">
        {start.data.secret}
      </p>
      <form
        className="flex flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          confirm.mutate(fd(new FormData(e.currentTarget), 'code').trim());
        }}
      >
        <Field
          label="Código de 6 dígitos"
          name="code"
          inputMode="numeric"
          autoComplete="one-time-code"
          required
        />
        <FormError message={confirm.error ? errorMessage(confirm.error) : null} />
        <Button type="submit" disabled={confirm.isPending}>
          Activar
        </Button>
      </form>
    </div>
  );
}

/** Desactivar: contraseña actual + un código de la app (o uno de recuperación). */
function DisableForm({ onDone, onCancel }: { onDone: () => void; onCancel: () => void }) {
  const [recovery, setRecovery] = useState(false);
  const disable = useMutation({
    mutationFn: (body: unknown) => api('/api/auth/mfa/disable', { method: 'POST', body }),
    onSuccess: onDone,
  });
  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        const f = new FormData(e.currentTarget);
        const value = fd(f, 'code').trim();
        disable.mutate({
          password: fd(f, 'password'),
          ...(recovery ? { recoveryCode: value.toLowerCase() } : { code: value }),
        });
      }}
    >
      <p className="text-sm text-muted-foreground">
        Para desactivarla confirma que eres tú: tu contraseña y un código de tu app. Cerraremos tus
        otras sesiones y te avisaremos por email.
      </p>
      <Field
        label="Contraseña actual"
        name="password"
        type="password"
        autoComplete="current-password"
        required
      />
      <Field
        key={recovery ? 'r' : 'c'}
        label={recovery ? 'Código de recuperación' : 'Código de la app'}
        name="code"
        inputMode={recovery ? 'text' : 'numeric'}
        autoComplete="one-time-code"
        placeholder={recovery ? 'xxxxx-xxxxx' : '123456'}
        required
      />
      <button
        type="button"
        className="self-start text-xs text-muted-foreground underline-offset-4 hover:underline"
        onClick={() => {
          setRecovery(!recovery);
        }}
      >
        {recovery ? 'Usar el código de la app' : 'Usar un código de recuperación'}
      </button>
      <FormError message={disable.error ? errorMessage(disable.error) : null} />
      <div className="flex gap-2">
        <Button type="submit" variant="destructive" disabled={disable.isPending}>
          Desactivar
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancelar
        </Button>
      </div>
    </form>
  );
}

/** Navegadores donde se marcó "Recordar este dispositivo": entran sin el código hasta que venzan. */
function TrustedDevices({ readOnly }: { readOnly: boolean }) {
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: TRUSTED_DEVICES_KEY,
    queryFn: () => api<TrustedDevice[]>('/api/auth/trusted-devices'),
  });
  const refresh = () => qc.invalidateQueries({ queryKey: TRUSTED_DEVICES_KEY });
  const revokeOne = useMutation({
    mutationFn: (id: string) => api(`/api/auth/trusted-devices/${id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      toast.success('Dispositivo quitado: te pediremos el código la próxima vez');
      await refresh();
    },
  });
  const revokeAll = useMutation({
    mutationFn: () => api('/api/auth/trusted-devices/revoke-all', { method: 'POST' }),
    onSuccess: async () => {
      toast.success('Listo: todos tus dispositivos te volverán a pedir el código');
      await refresh();
    },
  });
  const devices = Array.isArray(list.data) ? list.data : [];
  const error = list.error ?? revokeOne.error ?? revokeAll.error;
  return (
    <div className="flex flex-col gap-2 border-t pt-3">
      <p className="text-sm font-medium">Dispositivos de confianza</p>
      <p className="text-xs text-muted-foreground">
        En estos navegadores marcaste «Recordar este dispositivo»: entras sin el código de la app
        hasta la fecha indicada. Si cambias la contraseña o cierras todas tus sesiones, se quitan
        solos.
      </p>
      {error ? <FormError message={errorMessage(error)} /> : null}
      {list.isSuccess && devices.length === 0 ? (
        <p className="text-sm text-muted-foreground">No tienes ningún dispositivo de confianza.</p>
      ) : null}
      {devices.length > 0 ? (
        <ul aria-label="Dispositivos de confianza" className="flex flex-col gap-2">
          {devices.map((d) => (
            <li key={d.id} className="flex items-start gap-2 rounded-md border p-2 text-sm">
              <Laptop className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
              <div className="min-w-0 flex-1">
                <p className="font-medium">
                  {d.userAgent ?? 'Navegador desconocido'}
                  {d.current ? (
                    <Badge variant="secondary" className="ml-2">
                      Este navegador
                    </Badge>
                  ) : null}
                </p>
                <p className="text-xs text-muted-foreground">
                  Último uso: {fmtDateTime(d.lastUsedAt ?? d.createdAt)}
                  {d.ipPrefix ? ` · red ${d.ipPrefix}` : ''} · hasta {fmtDateTime(d.expiresAt)}
                </p>
              </div>
              {!readOnly ? (
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={`Quitar ${d.userAgent ?? 'dispositivo'}`}
                  disabled={revokeOne.isPending}
                  onClick={() => {
                    revokeOne.mutate(d.id);
                  }}
                >
                  Quitar
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
      {devices.length > 1 && !readOnly ? (
        <Button
          variant="outline"
          size="sm"
          className="self-start"
          disabled={revokeAll.isPending}
          onClick={() => {
            revokeAll.mutate();
          }}
        >
          Quitar todos
        </Button>
      ) : null}
    </div>
  );
}

/** Tarjeta "Seguridad": estado del 2FA y activar/desactivar cuando la persona puede elegir. */
function SecurityCard({ readOnly }: { readOnly: boolean }) {
  const qc = useQueryClient();
  const [mode, setMode] = useState<'idle' | 'enroll' | 'disable'>('idle');
  const status = useQuery({
    queryKey: MFA_STATUS_KEY,
    queryFn: () => api<MfaStatusResponse>('/api/auth/mfa/status'),
  });
  const refresh = async () => {
    setMode('idle');
    await Promise.all([
      qc.invalidateQueries({ queryKey: MFA_STATUS_KEY }),
      qc.invalidateQueries({ queryKey: ['me'] }),
    ]);
  };
  const s = status.data;
  return (
    <section aria-labelledby="security-title" className="flex flex-col gap-3 rounded-lg border p-4">
      <div className="flex flex-wrap items-center gap-2">
        <ShieldCheck className="size-4 text-muted-foreground" aria-hidden />
        <h3 id="security-title" className="font-medium">
          Seguridad
        </h3>
        {s ? (
          s.required && s.enabled ? (
            <Badge variant="success" className="ml-auto">
              Obligatoria para tu cuenta
            </Badge>
          ) : s.enabled ? (
            <Badge variant="success" className="ml-auto">
              Activada
            </Badge>
          ) : (
            <Badge variant="secondary" className="ml-auto">
              Desactivada
            </Badge>
          )
        ) : null}
      </div>
      <p className="text-sm text-muted-foreground">
        Verificación en dos pasos: además de la contraseña, al entrar te pedimos un código de tu
        móvil. Así nadie puede usar tu cuenta aunque conozca tu contraseña.
      </p>
      {status.error ? <FormError message={errorMessage(status.error)} /> : null}
      {s?.required ? <p className="text-sm">{mfaReasonText(s.reasons)}</p> : null}
      {s?.enabled ? (
        <p className="text-xs text-muted-foreground">
          Te quedan {s.recoveryCodesLeft} códigos de recuperación.
        </p>
      ) : null}
      {s && !readOnly && mode === 'idle' ? (
        !s.enabled ? (
          <Button
            className="self-start"
            onClick={() => {
              setMode('enroll');
            }}
          >
            Activar verificación en dos pasos
          </Button>
        ) : !s.required ? (
          <Button
            variant="outline"
            className="self-start"
            onClick={() => {
              setMode('disable');
            }}
          >
            Desactivar
          </Button>
        ) : null
      ) : null}
      {mode === 'enroll' ? (
        <EnrollSteps
          onDone={() => {
            toast.success('Verificación en dos pasos activada');
            void refresh();
          }}
        />
      ) : null}
      {mode === 'disable' ? (
        <DisableForm
          onDone={() => {
            toast.success('Verificación en dos pasos desactivada');
            void refresh();
          }}
          onCancel={() => {
            setMode('idle');
          }}
        />
      ) : null}
      {s?.enabled && mode === 'idle' ? <TrustedDevices readOnly={readOnly} /> : null}
    </section>
  );
}

/** "Mi perfil": foto (o iniciales), nombre, email y seguridad. Se abre desde el menú de la cuenta. */
export function ProfileDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const me = useMe();
  const qc = useQueryClient();
  const readOnly = Boolean(me.impersonation);
  const refresh = async (msg: string) => {
    await qc.invalidateQueries({ queryKey: ['me'] });
    toast.success(msg);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Mi perfil</DialogTitle>
          <DialogDescription>
            Tu foto aparece en el menú de tu cuenta y en la lista de tu equipo.
          </DialogDescription>
        </DialogHeader>
        <ImagePicker
          name={me.user.name}
          colorKey={me.user.email}
          src={me.user.avatarUrl}
          kind="photo"
          hasImage={Boolean(me.user.avatarUrl)}
          disabled={readOnly}
          hint={
            readOnly
              ? 'Mientras ves la consola como otra persona no se puede cambiar su foto.'
              : 'PNG, JPG o WebP. Se recorta a 256 px.'
          }
          onUpload={async (dataUrl) => {
            await api<AvatarResponse>('/api/me/avatar', {
              method: 'PUT',
              body: { contentBase64: dataUrl },
            });
            await refresh('Foto actualizada');
          }}
          onRemove={async () => {
            await api<AvatarResponse>('/api/me/avatar', { method: 'DELETE' });
            await refresh('Foto quitada');
          }}
        />
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 border-t pt-4 text-sm">
          <dt className="text-muted-foreground">Nombre</dt>
          <dd className="font-medium">{me.user.name}</dd>
          <dt className="text-muted-foreground">Email</dt>
          <dd className="break-all">{me.user.email}</dd>
        </dl>
        {open ? <SecurityCard readOnly={readOnly} /> : null}
      </DialogContent>
    </Dialog>
  );
}
